import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { REAL_NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { Action, ActionParams, DEFAULT_OP_UPDATE_VALUE_CONVERTER, Operation, OpUpdateValueConverter } from "./action.model";
import { OpAddToArray } from "./operations/opAddToArray";
import { OpCreate } from "./operations/opCreate";
import { OpDelete } from "./operations/opDelete";
import { OpRemoveFromArray } from "./operations/opRemoveFromArray";
import { OpUpdate } from "./operations/opUpdate";
import { ActionCacheService } from "./services/cache.service";

export class DefaultAction<ElementTypes = unknown, AppLogInfos = unknown, Context = unknown> implements Action<ElementTypes, AppLogInfos, Context> {

    /** @inheritdoc */
    public operations: Operation<ElementTypes, AppLogInfos, Context>[] = [];

    constructor(
        /** @inheritdoc */
        public params: ActionParams<ElementTypes, AppLogInfos, Context>,
        /** Local cache manager */
        protected _cacheManager: ActionCacheService<ElementTypes>,
        /** @inheritdoc */
        public context?: Context) {
    }

    /** @inheritdoc */
    public create<TypeName extends keyof ElementTypes>(elementTypeName: TypeName, objectToCreate: ElementTypes[TypeName]): ElementTypes[TypeName] {
        this._cacheManager.setTemporaryId(elementTypeName, objectToCreate);
        const op = new OpCreate<ElementTypes, TypeName, ElementTypes[TypeName], AppLogInfos, Context>(elementTypeName, objectToCreate);
        op.process(this);
        this.operations.push(op);
        return objectToCreate;
    }

    /** @inheritdoc */
    public delete<TypeName extends keyof ElementTypes>(elementTypeName: TypeName, objectToDelete: ElementTypes[TypeName]): void {
        if (this._cacheManager.getDTOCacheRepository(elementTypeName).canBeDeleted(objectToDelete)) {
            const op = new OpDelete<ElementTypes, TypeName, ElementTypes[TypeName], AppLogInfos, Context>(elementTypeName, objectToDelete);
            op.process(this);
            this.operations.push(op);
        }
    }

    /** @inheritdoc */
    public updateValueIfNeeded<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName],
        K extends keyof T,
        V extends T[K] = T[K]
    >(elementTypeName: TypeName, objectToUpdate: T, field: K, newValue: V): boolean {
        const dtoCacheRepository = this._cacheManager.getDTOCacheRepository(elementTypeName);
        const cachedObject = dtoCacheRepository.getUpToDateElement(objectToUpdate) as T;

        try {
            const op = this._updateValueIfNeededImpl(elementTypeName, cachedObject, field, newValue);
            if (op == null) {
                return false;
            }
            this.operations.push(op);
            return true;
        } finally {
            if (cachedObject !== objectToUpdate) {
                // If object in cache is different from the object passed to the action, we apply the same operation
                // but we don't register the operation as it is not required to cancel it
                this._updateValueIfNeededImpl(elementTypeName, objectToUpdate, field, newValue);
            }
        }
    }

    /** @returns the executed operation or undefined if no change */
    protected _updateValueIfNeededImpl<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName],
        K extends keyof T,
        V extends T[K] = T[K]
    >(elementTypeName: TypeName, objectToUpdate: T, field: K, newValue: V): OpUpdate<ElementTypes, TypeName, T, K, V, AppLogInfos, Context> | undefined {

        const oldValue = objectToUpdate[field];

        // Verify that a modification is required
        if (oldValue === newValue) {
            return undefined;
        }
        if (oldValue == null && newValue == null) {
            return undefined;
        }

        // We force the default value if the converter isn't given in the params 
        const opUpdateConverter: OpUpdateValueConverter<AppLogInfos, Context, ElementTypes, TypeName, T, K> = (this.params.opUpdateValueConverter ?? DEFAULT_OP_UPDATE_VALUE_CONVERTER) as OpUpdateValueConverter<AppLogInfos, Context, ElementTypes, TypeName, T, K>; // Forced because we do not know the types in opUpdateValueConverter but we require them for OpUpdate

        // Create Update operation
        const op = new OpUpdate<ElementTypes, TypeName, T, K, V, AppLogInfos, Context>(elementTypeName, objectToUpdate, field, newValue, opUpdateConverter);
        op.process(this);

        return op;
    }

    /** @inheritdoc */
    public updateValuesIfNeeded<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, newValues: Partial<T>): boolean {
        // Check "newValues" is defined
        if (newValues == null) { return false; }

        let wasUpdated = false;
        for (const field in newValues) {
            if (this.updateValueIfNeeded(elementTypeName, objectToUpdate, field, newValues[field] as T[Extract<keyof T, string>])) {
                wasUpdated = true;
            }
        }
        return wasUpdated;
    }

    /** @inheritdoc */
    public addToArray<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, field: keyof ElementTypes[TypeName],
        newValueToAdd: { addReference: { id: number } },
        ignoredDuplicate?: boolean // MAB: Using this option should be done only if you know what you do
    ): boolean {
        const dtoCacheRepository = this._cacheManager.getDTOCacheRepository(elementTypeName);
        const cachedObject = dtoCacheRepository.getUpToDateElement(objectToUpdate) as T;

        try {
            const op = this._addToArrayImpl(elementTypeName, cachedObject, field, newValueToAdd, ignoredDuplicate);
            if (op == null) {
                return false;
            }
            this.operations.push(op);
            return true;
        } finally {
            if (cachedObject !== objectToUpdate) {
                // If object in cache is different from the object passed to the action, we apply the same operation
                // but we don't register the operation as it is not required to cancel it
                this._addToArrayImpl(elementTypeName, objectToUpdate, field, newValueToAdd, ignoredDuplicate);
            }
        }
    }

    /** @returns the executed operation or undefined if no change */
    protected _addToArrayImpl<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, field: keyof ElementTypes[TypeName],
        newValueToAdd: { addReference: { id: number } },
        ignoredDuplicate?: boolean // MAB: Using this option should be done only if you know what you do
    ): OpAddToArray<ElementTypes, TypeName, T, AppLogInfos, Context> | undefined {

        const arrayObject: unknown[] = objectToUpdate[field] as unknown as unknown[];

        // Verify that a modification is required
        if (arrayObject) {
            if (!Array.isArray(arrayObject)) {
                // Not an array
                return undefined;
            }
            if (!ignoredDuplicate && arrayObject.includes(newValueToAdd.addReference.id)) {
                // id already in list and we don't want duplicates
                return undefined;
            }
        }

        // Create Update operation
        const op = new OpAddToArray<ElementTypes, TypeName, T, AppLogInfos, Context>(elementTypeName, objectToUpdate, field, newValueToAdd);
        op.process(this);

        return op;
    }


    /** @inheritdoc */
    public removeFromArray<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, field: keyof ElementTypes[TypeName],
        valueToRemove: { addReference: { id: number } } // TODO replace with AddToArrayValue once implemented
    ): boolean {
        if (objectToUpdate == null) { return false; }
        const dtoCacheRepository = this._cacheManager.getDTOCacheRepository(elementTypeName);
        const cachedObject = dtoCacheRepository.getUpToDateElement(objectToUpdate) as T;

        try {
            const op = this._removeFromArrayImpl(elementTypeName, cachedObject, field, valueToRemove);
            if (op == null) {
                return false;
            }
            this.operations.push(op);
            return true;
        } finally {
            if (cachedObject !== objectToUpdate) {
                // If object in cache is different from the object passed to the action, we apply the same operation
                // but we don't register the operation as it is not required to cancel it
                this._removeFromArrayImpl(elementTypeName, objectToUpdate, field, valueToRemove);
            }
        }
    }

    /** @returns the executed operation or undefined if no change */
    protected _removeFromArrayImpl<
        TypeName extends keyof ElementTypes,
        T extends ElementTypes[TypeName]
    >(elementTypeName: TypeName, objectToUpdate: T, field: keyof ElementTypes[TypeName],
        valueToRemove: { addReference: { id: number } } // TODO replace with AddToArrayValue once implemented
    ): OpRemoveFromArray<ElementTypes, TypeName, T, AppLogInfos, Context> | undefined {

        const arrayObject: unknown[] = objectToUpdate[field] as unknown as unknown[];
        // Verify that a modification is required
        if (arrayObject && (!Array.isArray(arrayObject) || !arrayObject.includes(valueToRemove.addReference.id))) {
            // id already in list
            return undefined;
        }

        // Create operation
        const op = new OpRemoveFromArray<ElementTypes, TypeName, T, AppLogInfos, Context>(elementTypeName, objectToUpdate, field, valueToRemove);
        op.process(this);

        return op;
    }

    /** @inheritdoc */
    public getCacheManager(): ActionCacheService<ElementTypes> {
        return this._cacheManager;
    }

    /** @inheritdoc */
    public setCacheManager(cacheService: ActionCacheService<ElementTypes>): void {
        this._cacheManager = cacheService;
    }

    /** @inheritdoc */
    public redo(): void {
        for (const operation of this.operations) {
            operation.actualize(this);
            operation.process(this);
        }
    }

    /** @inheritdoc */
    public undo(): void {
        for (let op = this.operations.length - 1; op >= 0; op--) {
            const operation = this.operations[op];
            operation.actualize(this);
            operation.undo(this);
        }
    }

    public clientTime: UnixTimestamp = REAL_NOW();
}
